/* MakNorte — sincronização do endereço do carrinho com a Storefront Cart API.
   O módulo não cria linhas, persiste CEP, calcula frete ou abre checkout. */
(function () {
  'use strict';

  const CFG = (window.MNV2 && window.MNV2) || {};
  const VERSION = /^\d{4}-\d{2}$/.test(String(CFG.storefrontApiVersion)) ? String(CFG.storefrontApiVersion) : '2026-07';
  const TIMEOUT_MS = Number.isFinite(Number(CFG.checkoutAddressTimeoutMs)) && Number(CFG.checkoutAddressTimeoutMs) > 0
    ? Number(CFG.checkoutAddressTimeoutMs) : 5000;
  const UF_RANGES = [[1000, 19999, 'SP'], [20000, 28999, 'RJ'], [29000, 29999, 'ES'], [30000, 39999, 'MG'], [40000, 48999, 'BA'], [49000, 49999, 'SE'], [50000, 56999, 'PE'], [57000, 57999, 'AL'], [58000, 58999, 'PB'], [59000, 59999, 'RN'], [60000, 63999, 'CE'], [64000, 64999, 'PI'], [65000, 65999, 'MA'], [66000, 68899, 'PA'], [68900, 68999, 'AP'], [69000, 69299, 'AM'], [69300, 69399, 'RR'], [69400, 69899, 'AM'], [69900, 69999, 'AC'], [70000, 72799, 'DF'], [72800, 72999, 'GO'], [73000, 73699, 'DF'], [73700, 76799, 'GO'], [76800, 76999, 'RO'], [77000, 77999, 'TO'], [78000, 78899, 'MT'], [79000, 79999, 'MS'], [80000, 87999, 'PR'], [88000, 89999, 'SC'], [90000, 99999, 'RS']];
  const CART_QUERY = `query MakNorteCheckoutCart($id: ID!) {
    cart(id: $id) {
      id totalQuantity note
      attributes { key value }
      discountCodes { code applicable }
      lines(first: 250) {
        nodes { id quantity attributes { key value } merchandise { ... on ProductVariant { id } } }
      }
      delivery {
        addresses {
          id selected oneTimeUse
          address { ... on CartDeliveryAddress { address1 address2 city company countryCode firstName lastName phone provinceCode zip } }
        }
      }
    }
  }`;
  const ADD_MUTATION = `mutation MakNorteCheckoutAddressAdd($id: ID!, $addresses: [CartSelectableAddressInput!]!) {
    cartDeliveryAddressesAdd(cartId: $id, addresses: $addresses) {
      userErrors { message code field }
      warnings { message code target }
      cart { id totalQuantity note attributes { key value } discountCodes { code applicable } lines(first: 250) { nodes { id quantity attributes { key value } merchandise { ... on ProductVariant { id } } } } delivery { addresses { id selected oneTimeUse address { ... on CartDeliveryAddress { address1 address2 city company countryCode firstName lastName phone provinceCode zip } } } } }
    }
  }`;
  const UPDATE_MUTATION = `mutation MakNorteCheckoutAddressUpdate($id: ID!, $addresses: [CartSelectableAddressUpdateInput!]!) {
    cartDeliveryAddressesUpdate(cartId: $id, addresses: $addresses) {
      userErrors { message code field }
      warnings { message code target }
      cart { id totalQuantity note attributes { key value } discountCodes { code applicable } lines(first: 250) { nodes { id quantity attributes { key value } merchandise { ... on ProductVariant { id } } } } delivery { addresses { id selected oneTimeUse address { ... on CartDeliveryAddress { address1 address2 city company countryCode firstName lastName phone provinceCode zip } } } } }
    }
  }`;

  let generation = 0;
  let activeController = null;
  let reloadRequired = false;
  const CLEARED_ADDRESS_FIELDS = ['address1', 'address2', 'city', 'company', 'firstName', 'lastName', 'phone'];

  function cleanCep(value) {
    const digits = String(value == null ? '' : value).replace(/\D/g, '');
    return /^\d{8}$/.test(digits) ? digits : '';
  }

  function provinceFor(digits) {
    const n = parseInt(digits.slice(0, 5), 10);
    const range = UF_RANGES.find(([min, max]) => n >= min && n <= max);
    return range ? range[2] : '';
  }

  function safeApiCode(value) {
    const code = String(value == null ? '' : value).trim();
    return /^[A-Z][A-Z_]{0,63}$/.test(code) ? code : null;
  }

  function diagnostic(code, details) {
    const item = { code };
    const status = Number(details && details.httpStatus);
    const apiCode = safeApiCode(details && details.apiCode);
    if (Number.isInteger(status) && status >= 100 && status <= 599) item.httpStatus = status;
    if (apiCode) item.apiCode = apiCode;
    return { errors: [item] };
  }

  function explicitError(code, extra) {
    const result = Object.assign({ ok: false, code }, extra || {});
    if (!Array.isArray(result.errors)) Object.assign(result, diagnostic(code, extra));
    return result;
  }

  function uncertainMutationError(code, extra) {
    reloadRequired = true;
    return explicitError(code, Object.assign({}, extra || {}, { mutated: true }));
  }

  function bridgeFailure(code, details) {
    const error = new Error(code);
    error.bridgeCode = code;
    if (details) {
      error.httpStatus = details.httpStatus;
      error.apiCode = details.apiCode;
    }
    return error;
  }

  function cartPath() {
    const origin = window.location && typeof window.location.origin === 'string' ? window.location.origin : '';
    if (!origin) throw new Error('cart_route_invalid');
    let configured = CFG.cartUrl;
    if (typeof configured !== 'string' || !configured) {
      const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
      configured = typeof root === 'string' && root.startsWith('/') ? `${root.replace(/\/$/, '/') }cart` : '/cart';
    }
    const url = new URL(configured, origin);
    if (url.origin !== origin) throw new Error('cart_route_invalid');
    url.pathname = url.pathname.endsWith('.js') ? url.pathname : `${url.pathname.replace(/\/$/, '')}.js`;
    url.search = '';
    url.hash = '';
    return url.toString();
  }

  function graphQlPath() {
    const origin = window.location && typeof window.location.origin === 'string' ? window.location.origin : '';
    return `${origin}/api/${VERSION}/graphql.json`;
  }

  function snapshot(cart) {
    return {
      totalQuantity: cart.totalQuantity,
      note: cart.note == null ? null : cart.note,
      attributes: Array.isArray(cart.attributes) ? cart.attributes.map(({ key, value }) => [key, value]) : [],
      discountCodes: Array.isArray(cart.discountCodes) ? cart.discountCodes.map(({ code }) => code) : [],
      lines: cart.lines && Array.isArray(cart.lines.nodes) ? cart.lines.nodes.map((line) => ({
        id: line.id, quantity: line.quantity, merchandise: line.merchandise && line.merchandise.id,
        attributes: Array.isArray(line.attributes) ? line.attributes.map(({ key, value }) => [key, value]) : [],
      })) : [],
    };
  }

  function preservation(before, after) {
    const left = snapshot(before);
    const right = snapshot(after);
    return {
      same: JSON.stringify(left) === JSON.stringify(right),
      lineCount: left.lines.length,
      totalQuantity: left.totalQuantity,
      linesUnchanged: JSON.stringify(left.lines) === JSON.stringify(right.lines),
      linePropertiesUnchanged: JSON.stringify(left.lines.map((line) => line.attributes)) === JSON.stringify(right.lines.map((line) => line.attributes)),
      noteUnchanged: left.note === right.note,
      attributesUnchanged: JSON.stringify(left.attributes) === JSON.stringify(right.attributes),
      discountCodesUnchanged: JSON.stringify(left.discountCodes) === JSON.stringify(right.discountCodes),
    };
  }

  function addressObject(address) {
    return address && address.address ? address.address : {};
  }

  function addressFieldsCleared(address) {
    const value = addressObject(address);
    return CLEARED_ADDRESS_FIELDS.every((key) => value[key] == null || value[key] === '');
  }

  function addressInput(digits, province, existing) {
    const old = addressObject(existing);
    const oldDigits = cleanCep(old.zip);
    const changed = Boolean(existing) && oldDigits !== digits;
    const deliveryAddress = { countryCode: 'BR', provinceCode: province, zip: `${digits.slice(0, 5)}-${digits.slice(5)}` };
    if (changed) {
      ['address1', 'address2', 'city', 'company', 'firstName', 'lastName', 'phone'].forEach((key) => { deliveryAddress[key] = null; });
    }
    return deliveryAddress;
  }

  function fallbackAddAddressInput(digits, province, existing) {
    const old = addressObject(existing);
    const deliveryAddress = { countryCode: 'BR', provinceCode: province, zip: `${digits.slice(0, 5)}-${digits.slice(5)}` };
    if (cleanCep(old.zip) === digits && String(old.countryCode || '').toUpperCase() === 'BR') {
      CLEARED_ADDRESS_FIELDS.forEach((key) => {
        if (Object.prototype.hasOwnProperty.call(old, key)) deliveryAddress[key] = old[key];
      });
    }
    return deliveryAddress;
  }

  function selectableAddressInput(operation, cartId, selected, address) {
    const selectable = { selected: true, oneTimeUse: true, validationStrategy: 'COUNTRY_CODE_ONLY', address: { deliveryAddress: address } };
    return operation === 'update'
      ? { id: cartId, addresses: [{ id: selected.id, ...selectable }] }
      : { id: cartId, addresses: [selectable] };
  }

  function canRecoverInvalidAddressId(payload, addresses) {
    return Array.isArray(addresses) && addresses.length < 20
      && Array.isArray(payload && payload.userErrors)
      && payload.userErrors.length === 1
      && payload.userErrors[0] && payload.userErrors[0].code === 'INVALID_DELIVERY_ADDRESS_ID'
      && Array.isArray(payload && payload.warnings) && payload.warnings.length === 0;
  }

  async function withDeadline(task, deadline) {
    return Promise.race([task, deadline]);
  }

  async function readJson(response) {
    try { return await response.json(); } catch (_error) { return null; }
  }

  async function fetchCart(controller) {
    const response = await fetch(cartPath(), { credentials: 'same-origin', headers: { Accept: 'application/json' }, signal: controller.signal });
    const cart = await readJson(response);
    if (!response.ok || !cart || typeof cart.token !== 'string' || !cart.token || !Number.isInteger(cart.item_count) || cart.item_count < 0 || !Array.isArray(cart.items)) throw bridgeFailure('cart_unavailable', { httpStatus: response.status });
    return cart;
  }

  async function graphQl(query, variables, controller) {
    const response = await fetch(graphQlPath(), {
      method: 'POST', credentials: 'same-origin', signal: controller.signal,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ query, variables }),
    });
    const body = await readJson(response);
    const apiCode = Array.isArray(body && body.errors) && body.errors.length
      ? body.errors.map((error) => error && error.extensions && error.extensions.code).find((code) => safeApiCode(code))
      : null;
    if (!response.ok || !body || (Array.isArray(body.errors) && body.errors.length)) throw bridgeFailure('graphql_unavailable', { httpStatus: response.status, apiCode });
    return body.data;
  }

  function resultForCart(cart, cartId) {
    const opaquePart = (value) => String(value == null ? '' : value).split('?')[0];
    const cartReturned = Boolean(cart);
    const opaqueIdSame = cartReturned && opaquePart(cart.id) !== '' && opaquePart(cart.id) === opaquePart(cartId);
    if (!cart || cart.id !== cartId) return explicitError('same_cart_unconfirmed', { mutated: false, cartReturned, opaqueIdSame });
    return cart;
  }

  async function sync(rawCep) {
    const digits = cleanCep(rawCep);
    if (!rawCep) return { ok: true, skipped: 'missing_cep' };
    if (!digits) return explicitError('invalid_cep', { mutated: false });
    const province = provinceFor(digits);
    if (!province) return explicitError('unknown_cep', { mutated: false });
    if (activeController) return explicitError('busy', { mutated: false });
    if (reloadRequired) return explicitError('reload_required', { mutated: false });
    const requestGeneration = ++generation;
    const controller = new AbortController();
    activeController = controller;
    let mutationSent = false;
    let timedOut = false;
    let rejectDeadline;
    const deadline = new Promise((_resolve, reject) => { rejectDeadline = reject; });
    const deadlineTimer = setTimeout(() => { timedOut = true; controller.abort(); rejectDeadline(new Error('timeout')); }, TIMEOUT_MS);
    const ensureCurrent = () => {
      if (requestGeneration !== generation) throw new Error('superseded');
    };
    try {
      const ajaxCart = await withDeadline(fetchCart(controller), deadline);
      ensureCurrent();
      const cartId = `gid://shopify/Cart/${ajaxCart.token}`;
      if (ajaxCart.item_count === 0) return { ok: true, skipped: 'empty_cart', sameCart: true };
      const beforeData = await withDeadline(graphQl(CART_QUERY, { id: cartId }, controller), deadline);
      ensureCurrent();
      const before = resultForCart(beforeData && beforeData.cart, cartId);
      if (before && before.ok === false) return before;
      const addresses = before.delivery && Array.isArray(before.delivery.addresses) ? before.delivery.addresses : [];
      const selected = addresses.find((address) => address.selected) || addresses[0] || null;
      let operation = selected ? 'update' : 'add';
      const address = addressInput(digits, province, selected);
      let variables = selectableAddressInput(operation, cartId, selected, address);
      ensureCurrent();
      mutationSent = true;
      let mutationData = await withDeadline(graphQl(operation === 'update' ? UPDATE_MUTATION : ADD_MUTATION, variables, controller), deadline);
      let payload = mutationData && mutationData[operation === 'update' ? 'cartDeliveryAddressesUpdate' : 'cartDeliveryAddressesAdd'];
      if (!payload) {
        reloadRequired = true;
        return explicitError('reload_required', { mutated: true });
      }
      if (operation === 'update' && canRecoverInvalidAddressId(payload, addresses)) {
        operation = 'add';
        variables = selectableAddressInput(operation, cartId, null, fallbackAddAddressInput(digits, province, selected));
        mutationData = await withDeadline(graphQl(ADD_MUTATION, variables, controller), deadline);
        payload = mutationData && mutationData.cartDeliveryAddressesAdd;
        if (!payload) {
          reloadRequired = true;
          return explicitError('reload_required', { mutated: true });
        }
      }
      if (Array.isArray(payload.userErrors) && payload.userErrors.length) {
        const apiCode = payload.userErrors.map((error) => error && error.code).find((code) => safeApiCode(code));
        return explicitError('address_rejected', { mutated: false, ...diagnostic('address_rejected', { apiCode }) });
      }
      if (Array.isArray(payload.warnings) && payload.warnings.length) {
        const apiCode = payload.warnings.map((warning) => warning && warning.code).find((code) => safeApiCode(code));
        return uncertainMutationError('address_warning', diagnostic('address_warning', { apiCode }));
      }
      ensureCurrent();
      const after = resultForCart(payload.cart, cartId);
      if (after && after.ok === false) return uncertainMutationError(after.code, after);
      const afterAddresses = after.delivery && Array.isArray(after.delivery.addresses) ? after.delivery.addresses : [];
      const selectedAddresses = afterAddresses.filter((item) => item && item.selected);
      const afterAddress = selectedAddresses[0] || null;
      const expectedZip = `${digits.slice(0, 5)}-${digits.slice(5)}`;
      if (selectedAddresses.length !== 1 || !afterAddress || !afterAddress.oneTimeUse || addressObject(afterAddress).zip !== expectedZip || addressObject(afterAddress).provinceCode !== province || addressObject(afterAddress).countryCode !== 'BR') return uncertainMutationError('address_unconfirmed');
      if (selected && cleanCep(addressObject(selected).zip) !== digits && !addressFieldsCleared(afterAddress)) return uncertainMutationError('address_unconfirmed');
      const preserved = preservation(before, after);
      if (!preserved.same) return uncertainMutationError('cart_changed', { preserved });
      return { ok: true, sameCart: true, operation, preserved };
    } catch (error) {
      if (mutationSent) reloadRequired = true;
      const code = reloadRequired ? 'reload_required' : requestGeneration !== generation || error && error.message === 'superseded' ? 'superseded' : timedOut || error && error.message === 'timeout' ? 'timeout' : error && error.message === 'cart_route_invalid' ? 'cart_route_invalid' : 'bridge_unavailable';
      const details = diagnostic(code, { httpStatus: error && error.httpStatus, apiCode: error && error.apiCode });
      return explicitError(code, { mutated: mutationSent, ...details });
    } finally {
      clearTimeout(deadlineTimer);
      if (activeController === controller) activeController = null;
    }
  }

  window.MNCheckoutAddress = { prepare: sync, sync };
})();
